import {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Typography,
  notification,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  SaveOutlined,
  TagOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {useSelector} from 'react-redux';

import type {
  NewProgram,
  UpdateNewProgramInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/program/graphql/program.interface';
import {ProgramsNewService} from './ProgramsNewService';

const {Title, Text} = Typography;

const programsService = new ProgramsNewService();

export const ProgramEdit = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const {programId} = useParams<{programId: string}>();

  const [originalProg, setOriginalProg] = useState<any | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);

  const currentUser = useSelector((state: any) => state.user?.userProfile);
  const formValues = Form.useWatch([], form);

  // ── Load data ──
  useEffect(() => {
    programsService
      .getNewProgramById(programId)
      .then(prog => {
        if (!prog) {
          setIsError(true);
          return;
        }
        setOriginalProg(prog);
        form.setFieldsValue({
          name: prog.name,
        });
        setIsFetching(false);
      })
      .catch(() => {
        setIsError(true);
        setIsFetching(false);
      });
  }, [programId]);

  // ── Submit ──
  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const updateInput: UpdateNewProgramInput = {
        id: programId,
        name: values.name.trim(),
        createdBy: currentUser?.email ?? 'unknown',
      };

      await programsService.updateNewProgram(updateInput);

      notification.success({
        message: 'Program Updated!',
        description: `"${values.name}" has been updated successfully.`,
        duration: 4,
      });

      setTimeout(() => history.push('/programs'), 1200);
    } catch (err: any) {
      if (err?.errorFields) return; // inline validation
      notification.error({
        message: 'Update Failed',
        description: err?.message || 'An unexpected error occurred.',
        duration: 6,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── States ──
  if (isError) {
    return (
      <div style={{padding: 40}}>
        <Alert
          type="error"
          message="Failed to load program"
          description="Please go back and try again."
          action={<Button onClick={() => history.goBack()}>Go Back</Button>}
        />
      </div>
    );
  }

  if (isFetching) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 80}}>
        <Spin size="large" tip="Loading program..." />
      </div>
    );
  }

  const isChanged = formValues?.name !== originalProg?.name;

  return (
    <div style={{padding: '24px 32px', maxWidth: 720, margin: '0 auto'}}>
      <Title level={2} style={{textAlign: 'center', marginBottom: 32}}>
        <EditOutlined style={{marginRight: 8}} />
        Edit Program
      </Title>

      <Form form={form} layout="vertical">
        {/* ── Program Name ── */}
        <Card
          title={
            <>
              <TagOutlined /> &nbsp; Program Information
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="name"
                label="Program Name"
                rules={[{required: true, message: 'Program name is required'}]}>
                <Input placeholder="e.g. English Program" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Diff Summary ── */}
        <Card
          title={
            <>
              <CheckCircleOutlined /> &nbsp; Changes Summary
            </>
          }
          style={{marginBottom: 24}}>
          {!isChanged ? (
            <Text type="secondary">No changes made yet.</Text>
          ) : (
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <Text type="secondary">Program Name</Text>
                <br />
                <Text delete type="secondary" style={{marginRight: 8}}>
                  {originalProg?.name}
                </Text>
                <Text strong type="success">
                  {formValues?.name}
                </Text>
              </Col>
            </Row>
          )}
        </Card>

        {/* Actions */}
        <div style={{textAlign: 'center', marginTop: 32}}>
          <Space size="middle">
            <Popconfirm
              title="Are you sure you want to delete this program?"
              onConfirm={async () => {
                setIsSubmitting(true);
                try {
                  await programsService.softDeleteNewProgram(programId);
                  notification.success({
                    message: 'Program deleted successfully',
                  });
                  history.push('/programs');
                } catch (err: any) {
                  notification.error({
                    message: 'Failed to delete program',
                    description: err?.message,
                  });
                  setIsSubmitting(false);
                }
              }}>
              <Button size="large" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>

            <Button size="large" onClick={() => history.goBack()}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={isSubmitting}
              disabled={!isChanged}
              onClick={handleUpdate}
              style={{minWidth: 180}}>
              Save Changes
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};
